import * as React from "react";
import { useFetcher } from "react-router";

export interface VehicleOption {
  id: string;
  display: string;
}

export interface ObdVehicleCompatibilityResult {
  compatible: boolean;
  compatibility: string;
  description: string | null;
  image: string | null;
  graphic: string | number | null;
}

// What the /vehicle-compatibility proxy returns in place of a non-2xx upstream response.
interface VehicleCompatibilityError {
  error: number;
}

type VehicleOptionsResponse = { data: VehicleOption[] } | VehicleCompatibilityError;

// A failed list lookup reads as an empty list — the select just shows "no results".
function optionsOf(response: VehicleOptionsResponse | undefined): VehicleOption[] {
  return response && "data" in response ? response.data : [];
}

// Scroll offset of the drawer's scroll container, captured on close and restored on
// reopen. Kept in a ref, not state — written once per close, never rendered.
export interface ObdCompatibilityCheckerScrollPositions {
  drawer: number;
}

interface ObdCompatibilityCheckerContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  make: VehicleOption | null;
  model: VehicleOption | null;
  year: VehicleOption | null;
  step: "select" | "result";
  isLoadingResult: boolean;
  result: ObdVehicleCompatibilityResult | undefined;
  isResultFailed: boolean;
  isVehicleNotListed: boolean;
  makes: VehicleOption[];
  models: VehicleOption[];
  years: VehicleOption[];
  isLoadingModels: boolean;
  isLoadingYears: boolean;
  selectMake: (id: string) => void;
  selectModel: (id: string) => void;
  selectYear: (id: string) => void;
  confirm: () => void;
  reportVehicleNotListed: () => void;
  checkAnotherVehicle: () => void;
  scrollPositionsRef: React.RefObject<ObdCompatibilityCheckerScrollPositions>;
}

const ObdCompatibilityCheckerContext = React.createContext<ObdCompatibilityCheckerContextValue | null>(null);

// Mounted once in root.tsx (along with the drawer) so every trigger — the Drive PDP
// section, the header nav and the footer — shares one state and one drawer.
export function ObdCompatibilityCheckerProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [make, setMake] = React.useState<VehicleOption | null>(null);
  const [model, setModel] = React.useState<VehicleOption | null>(null);
  const [year, setYear] = React.useState<VehicleOption | null>(null);
  const [step, setStep] = React.useState<"select" | "result">("select");
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [isVehicleNotListed, setIsVehicleNotListed] = React.useState(false);
  const scrollPositionsRef = React.useRef<ObdCompatibilityCheckerScrollPositions>({ drawer: 0 });

  const makesFetcher = useFetcher<VehicleOptionsResponse>();
  const modelsFetcher = useFetcher<VehicleOptionsResponse>();
  const yearsFetcher = useFetcher<VehicleOptionsResponse>();
  const resultFetcher = useFetcher<ObdVehicleCompatibilityResult | VehicleCompatibilityError>();
  const makes = optionsOf(makesFetcher.data);
  const models = optionsOf(modelsFetcher.data);
  const years = optionsOf(yearsFetcher.data);
  const isResultFailed = !!resultFetcher.data && "error" in resultFetcher.data;

  // Clears isConfirming once fresh data actually lands, not on a timer/effect — avoids
  // flashing the previous vehicle's result for a render.
  const [seenResultData, setSeenResultData] = React.useState(resultFetcher.data);
  if (resultFetcher.data !== seenResultData) {
    setSeenResultData(resultFetcher.data);
    if (resultFetcher.data) setIsConfirming(false);
  }

  React.useEffect(() => {
    if (makesFetcher.state === "idle" && !makesFetcher.data) {
      makesFetcher.load("/vehicle-compatibility/makes");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once, on mount
  }, []);

  function selectMake(id: string) {
    const selected = makes.find((m) => m.id === id);
    if (!selected) return;
    setMake(selected);
    setModel(null);
    setYear(null);
    modelsFetcher.load(`/vehicle-compatibility/makes/${selected.id}/models?make=${encodeURIComponent(selected.display)}`);
  }

  function selectModel(id: string) {
    const selected = models.find((m) => m.id === id);
    if (!selected || !make) return;
    setModel(selected);
    setYear(null);
    yearsFetcher.load(
      `/vehicle-compatibility/makes/${make.id}/models/${selected.id}/years?make=${encodeURIComponent(make.display)}&model=${encodeURIComponent(selected.display)}`,
    );
  }

  function selectYear(id: string) {
    const selected = years.find((y) => y.id === id);
    if (!selected) return;
    setYear(selected);
  }

  function confirm() {
    if (!make || !model || !year) return;
    setIsVehicleNotListed(false);
    setIsConfirming(true);
    resultFetcher.load(
      `/vehicle-compatibility/makes/${make.id}/models/${model.id}/years/${year.id}?make=${encodeURIComponent(make.display)}&model=${encodeURIComponent(model.display)}`,
    );
    setStep("result");
  }

  // Skips the lookup and goes straight to the "Chat With Us" result — there's no
  // vehicle to look up, so no result to wait on.
  function reportVehicleNotListed() {
    setIsVehicleNotListed(true);
    setStep("result");
  }

  // "Check Another Vehicle" link on the confirmed screen — back to the select step with a
  // blank make/model/year.
  function checkAnotherVehicle() {
    setMake(null);
    setModel(null);
    setYear(null);
    setIsVehicleNotListed(false);
    setStep("select");
  }

  const value: ObdCompatibilityCheckerContextValue = {
    open,
    // Closing and reopening the drawer leaves every piece of state alone, so it reopens
    // on whatever step and selections the user left it on.
    setOpen,
    make,
    model,
    year,
    step,
    isLoadingResult: !isVehicleNotListed && (isConfirming || resultFetcher.state !== "idle"),
    result: resultFetcher.data && !("error" in resultFetcher.data) ? resultFetcher.data : undefined,
    isResultFailed,
    isVehicleNotListed,
    makes,
    models,
    years,
    isLoadingModels: modelsFetcher.state !== "idle",
    isLoadingYears: yearsFetcher.state !== "idle",
    selectMake,
    selectModel,
    selectYear,
    confirm,
    reportVehicleNotListed,
    checkAnotherVehicle,
    scrollPositionsRef,
  };

  return <ObdCompatibilityCheckerContext.Provider value={value}>{children}</ObdCompatibilityCheckerContext.Provider>;
}

export function useObdCompatibilityChecker() {
  const context = React.useContext(ObdCompatibilityCheckerContext);
  if (!context) throw new Error("useObdCompatibilityChecker must be used within ObdCompatibilityCheckerProvider");
  return context;
}

// For links that only need to open the drawer (header nav, footer). Returns undefined
// outside the provider instead of throwing, so those components still render standalone
// (e.g. in unit tests) — the link then just falls back to its href.
export function useOpenObdCompatibilityChecker(): (() => void) | undefined {
  const context = React.useContext(ObdCompatibilityCheckerContext);
  const setOpen = context?.setOpen;
  return setOpen ? () => setOpen(true) : undefined;
}
